import invariant from 'tiny-invariant'
import { ChainId } from '../constants'
import { validateAndParseAddress } from '../utils'
import { Currency } from './currency'
import { WETH } from '../cypherium_address.json'

/**
 * Represents an ERC20 token with a unique address and some metadata.
 */
export class Token extends Currency {
  public readonly chainId: ChainId
  public readonly address: string

  public constructor(chainId: ChainId, address: string, decimals: number, symbol?: string, name?: string) {
    super(decimals, symbol, name)
    this.chainId = chainId
    this.address = validateAndParseAddress(address)
  }

  /**
   * Returns true if the two tokens are equivalent, i.e. have the same chainId and address.
   * @param other other token to compare
   */
  public equals(other: Token): boolean {
    // short circuit on reference equality
    if (this === other) {
      return true
    }
    return this.chainId === other.chainId && this.address === other.address
  }

  /**
   * Returns true if the address of this token sorts before the address of the other token
   * @param other other token to compare
   * @throws if the tokens have the same address
   * @throws if the tokens are on different chains
   */
  public sortsBefore(other: Token): boolean {
    invariant(this.chainId === other.chainId, 'CHAIN_IDS')
    invariant(this.address !== other.address, 'ADDRESSES')
    return this.address.toLowerCase() < other.address.toLowerCase()
  }
}

/**
 * Compares two currencies for equality
 */
export function currencyEquals(currencyA: Currency, currencyB: Currency): boolean {
  if (currencyA instanceof Token && currencyB instanceof Token) {
    return currencyA.equals(currencyB)
  } else if (currencyA instanceof Token) {
    return false
  } else if (currencyB instanceof Token) {
    return false
  } else {
    return currencyA === currencyB
  }
}

export const WDEV = {
  [ChainId.CYPHER_MAINNET]: new Token(
    ChainId.CYPHER_MAINNET,
    '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    18,
    'WETH',
    'Wrapped Ether'
  ),
  [ChainId.CYPHER_ROPSTEN]: new Token(
    ChainId.CYPHER_ROPSTEN,
    '0x021e05349E4106Ddf7afFF4d1afa5E9160de0Ea2',
    18,
    'WDEV',
    'Wrapped Dev'
  ),
  [ChainId.CYPHER_RINKEBY]: new Token(ChainId.CYPHER_RINKEBY, WETH, 18, 'WDEV', 'Wrapped Dev'),
  [ChainId.CYPHER_GÖRLI]: new Token(ChainId.CYPHER_GÖRLI, WETH, 18, 'WDEV', 'Wrapped Dev'),
  [ChainId.CYPHER_KOVAN]: new Token(ChainId.CYPHER_KOVAN, WETH, 18, 'WDEV', 'Wrapped Dev')
}
